import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { post } from '../api'
import { LAST_TENANT_SLUG_KEY } from './last-slug'

interface LoginResponse {
  ok: boolean
  agent: { id: string; tenantId: string; role: 'agent' | 'admin' }
}

export function AgentLogin() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      await post<LoginResponse>('/api/agent/login', {
        slug, email, password, totp,
      })
      // Remember the slug only on success — typing a wrong slug shouldn't
      // pin a user to a tenant they can't log into.
      localStorage.setItem(LAST_TENANT_SLUG_KEY, slug)
      navigate(`/t/${slug}/agent/console`)
    } catch (err) {
      const code = (err as { code?: string }).code
      setError(
        code === 'MFA_INVALID'
          ? 'Authenticator code invalid.'
          : 'Invalid credentials.',
      )
    }
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
      <h1 className="text-xl font-semibold mb-1">Agent sign in</h1>
      <p className="text-sm text-slate-500 mb-1 font-mono">/t/{slug || '…'}</p>
      <p className="text-sm text-slate-600 mb-6">
        Sign in with your work email + authenticator code.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email" value={email} onChange={setEmail} type="email" autoComplete="username" />
        <Field label="Password" value={password} onChange={setPassword} type="password" autoComplete="current-password" />
        <Field label="6-digit code" value={totp} onChange={setTotp} pattern="\d{6}" autoComplete="one-time-code" />
        <button
          type="submit"
          className="w-full rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700"
        >
          Sign in
        </button>
      </form>
      {error && (
        <div className="mt-6 p-4 bg-rose-50 border border-rose-100 rounded-lg text-sm text-rose-900">
          {error}
        </div>
      )}
    </div>
  )
}

function Field(props: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  pattern?: string
  autoComplete?: string
}) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{props.label}</span>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        type={props.type ?? 'text'}
        pattern={props.pattern}
        autoComplete={props.autoComplete}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        required
      />
    </label>
  )
}
